import { createContext, useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';

import { isServer } from 'src/shared/helpers/environment';
import { getRoute } from 'src/shared/helpers/url';
import { RouteDescriptor } from 'src/shared/route';
import { Page } from 'src/shared/telemetry';

type CurrentPage = Page | null;

type SegmentHistoryContext = {
  page: CurrentPage;
  setPage: ((page: CurrentPage) => void) | null;
};

const defaultValue: SegmentHistoryContext = {
  page: null,
  setPage: null,
};

const context = createContext(defaultValue);

const trackPage = (route: RouteDescriptor) => {
  if (isServer() || !route.page || !window.analytics) return;

  analytics.page(route.page);
};

const useSegmentHistory = () => {
  const { setPage } = useContext(context);
  const history = useHistory();

  const setAndTrackPage = useCallback(
    (pathname: string) => {
      const route = getRoute(pathname);
      if (!route) return;

      setPage?.(route.page ?? null);
      trackPage(route);
    },
    [setPage],
  );

  useEffect(() => {
    const unlisten = history.listen(location =>
      setAndTrackPage(location.pathname),
    );

    return () => unlisten();
  }, [history, setAndTrackPage]);

  const route = getRoute(history.location.pathname);

  if (route) trackPage(route);
};

const { Provider } = context;

export {
  CurrentPage,
  Provider,
  SegmentHistoryContext,
  context,
  defaultValue,
  useSegmentHistory,
};
